export type SpritesheetFrame = {
  frame: { x: number; y: number; w: number; h: number };
  rotated?: boolean;
  trimmed?: boolean;
  spriteSourceSize?: { x: number; y: number; w: number; h: number };
  sourceSize?: { w: number; h: number };
  [key: string]: unknown;
};

export type PixiSpritesheetData = {
  frames: Record<string, SpritesheetFrame>;
  animations: Record<string, string[]>;
  meta: { scale: number | string; image?: string; size?: { w: number; h: number }; [key: string]: unknown };
};

function isFrameMap(frames: unknown): frames is Record<string, SpritesheetFrame> {
  return !!frames && typeof frames === 'object' && !Array.isArray(frames);
}

function frameFromPackedItem(item: Record<string, unknown>): SpritesheetFrame | null {
  const frame = item.frame;
  if (!frame || typeof frame !== 'object') return null;
  return {
    ...item,
    frame: frame as SpritesheetFrame['frame'],
    rotated: Boolean(item.rotated),
    trimmed: Boolean(item.trimmed),
    spriteSourceSize: item.spriteSourceSize as SpritesheetFrame['spriteSourceSize'],
    sourceSize: item.sourceSize as SpritesheetFrame['sourceSize'],
  };
}

function collectArrayFrames(
  list: unknown[],
  frames: Record<string, SpritesheetFrame>,
  animations: Record<string, string[]>,
): void {
  for (const raw of list) {
    if (!raw || typeof raw !== 'object') continue;
    const item = raw as Record<string, unknown>;
    const name = String(item.filename || item.name || '');
    if (!name) continue;
    const packed = frameFromPackedItem(item);
    if (!packed) continue;
    frames[name] = packed;
    const slash = name.lastIndexOf('/');
    if (slash > 0) {
      const anim = name.slice(0, slash);
      if (!animations[anim]) animations[anim] = [];
      if (!animations[anim].includes(name)) animations[anim].push(name);
    }
  }
}

/**
 * Pixi `Spritesheet` reads `data.meta.scale` and a name→frame `frames` map.
 * TexturePacker multi-atlas (`textures[].frames` as an array) is accepted and
 * rewritten to that hash. Always returns a `meta.scale` so construction cannot
 * throw `Cannot read properties of undefined (reading 'scale')`.
 */
export function normalizeSpritesheetData(raw: unknown): PixiSpritesheetData {
  if (!raw || typeof raw !== 'object') {
    return { frames: {}, animations: {}, meta: { scale: 1 } };
  }

  const json = raw as Record<string, unknown> & {
    frames?: unknown;
    animations?: Record<string, string[]>;
    meta?: Record<string, unknown>;
    textures?: Array<Record<string, unknown>>;
  };

  if (isFrameMap(json.frames)) {
    return {
      ...json,
      frames: json.frames,
      animations: { ...(json.animations || {}) },
      meta: { scale: 1, ...(json.meta || {}) },
    };
  }

  const frames: Record<string, SpritesheetFrame> = {};
  const animations: Record<string, string[]> = { ...(json.animations || {}) };

  if (Array.isArray(json.textures)) {
    for (const sheet of json.textures) {
      if (!sheet || typeof sheet !== 'object') continue;
      if (Array.isArray(sheet.frames)) {
        collectArrayFrames(sheet.frames, frames, animations);
      }
    }
    const first = json.textures[0] || {};
    const scale = first.scale ?? json.meta?.scale ?? 1;
    return {
      frames,
      animations,
      meta: {
        scale: typeof scale === 'number' || typeof scale === 'string' ? scale : 1,
        image: typeof first.image === 'string' ? first.image : undefined,
        size: first.size as { w: number; h: number } | undefined,
        format: first.format,
        ...(json.meta || {}),
      },
    };
  }

  if (Array.isArray(json.frames)) {
    collectArrayFrames(json.frames, frames, animations);
    return {
      frames,
      animations,
      meta: { scale: 1, ...(json.meta || {}) },
    };
  }

  return {
    frames: {},
    animations,
    meta: { scale: 1, ...(json.meta || {}) },
  };
}
